/**
 * DragToRotate Manager
 *
 * Provides a drag interaction for rotating a TransformNode:
 *
 * Pointer-down on the object's meshes starts a rotation drag. Horizontal
 * mouse movement rotates around the world Y axis, vertical movement rotates
 * around the camera-right axis (projected). When axes are constrained, only
 * the allowed axes are affected.
 *
 * Used by:
 * - `Rotatable` behavior at runtime (viewer)
 */

import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { IPointerEvent } from "@babylonjs/core/Events/deviceInputEvents";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import type { PointerInfo } from "@babylonjs/core/Events/pointerEvents";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { ensureRotationQuaternion } from "../utils/TransformUtils";
import { Observable } from "@babylonjs/core/Misc/observable";
import type { Axis } from "../scenedata/common";
import { VelocityTracker, glideSweep, glideRate } from "../utils/dynamics";

// ============================================================================
// Types
// ============================================================================

export interface DragToRotateOptions {
    /** Allowed rotation axes. When omitted, all axes are allowed. */
    axes?: Axis[];
    /** Called when a drag starts. Use to select the object in the editor. */
    onGrab?: () => void;
    /** Enable snap support (Alt = 15° snap, Alt+Shift = 45° snap). Default: false. */
    snap?: boolean;
    /**
     * Release momentum: spin-down time constant in seconds. On release the
     * flick keeps spinning, decaying per the closed-form kernel in
     * utils/dynamics (spec: docs-internal/MANIPULATION_MOMENTUM_SPEC.md).
     * 0 or omitted = stops dead on release (classic behavior). This is a
     * manager OPTION, never read from behavior data here — the editor's
     * drag instances must never glide (their drag-end capture assumes the
     * object rests at release).
     */
    momentumDamping?: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Radians per pixel of mouse movement. */
const ROTATION_SPEED = 0.01;

// Momentum constants — load-bearing spec values, mirrored in
// docs-internal/MANIPULATION_MOMENTUM_SPEC.md. Do not tune casually.
/** Glide stops when both angular rates fall below this (rad/s ≈ 2°/s). */
const MOMENTUM_STOP_RATE = 0.035;
/** Release-velocity clamp (rad/s ≈ 720°/s) — tames single-frame flick spikes. */
const MOMENTUM_MAX_RATE = 4 * Math.PI;

// Snap constants (matching gizmo-snap-service)
const SNAP_ROTATION_FINE = Math.PI / 12;    // 15 degrees
const SNAP_ROTATION_COARSE = Math.PI / 4;   // 45 degrees

function roundTo(value: number, step: number): number {
    return Math.round(value / step) * step;
}

/**
 * Compose the target rotation from a drag's start quaternion and accumulated
 * rotation amounts. This is the SINGLE source of truth for both the live drag
 * (`_updateDrag`) and the release glide (`_glideStep`) — the two must stay
 * bit-identical or the pose would jump at the drag→glide handoff. Spec:
 * docs-internal/MANIPULATION_MOMENTUM_SPEC.md ("Rotation composition").
 *
 * Constrained mode (axes non-empty): rotate around the object's local axes,
 * extracted from `startQuat` so they remain stable throughout the drag. Mouse
 * movement maps to each allowed local axis by projecting how much that axis's
 * rotation would move the object on screen:
 *   - Rotation around an axis that points up on screen → responds to horizontal drag
 *   - Rotation around an axis that points right on screen → responds to vertical drag
 * using the camera's up/right directions (`camUp`/`camRight`). When an axis is
 * diagonal to the camera (both dot products ≈ 0.7), the blended amount has no
 * clean geometric meaning — a "15° snap" on this scalar won't produce exactly
 * 15° of rotation. Acceptable for typical use where constrained axes are
 * near-aligned to the view.
 *
 * Unconstrained mode: horizontal drag → world Y, vertical drag → `rightAxis`
 * (camera-right flattened onto XZ): yRot · xRot · startQuat.
 *
 * `snapStep` (radians) applies snapping — only ever passed for active
 * gestures, never during a glide (modifier keys belong to active gestures).
 */
function composeDragRotation(
    startQuat: Quaternion,
    accumX: number,
    accumY: number,
    axes: Axis[] | undefined,
    camUp: Vector3,
    camRight: Vector3,
    rightAxis: Vector3,
    snapStep?: number,
): Quaternion {
    if (axes && axes.length > 0) {
        const localX = Vector3.Right();
        const localY = Vector3.Up();
        const localZ = Vector3.Forward();
        localX.rotateByQuaternionToRef(startQuat, localX);
        localY.rotateByQuaternionToRef(startQuat, localY);
        localZ.rotateByQuaternionToRef(startQuat, localZ);

        const localAxes: Vector3[] = [];
        if (axes.includes("x")) localAxes.push(localX);
        if (axes.includes("y")) localAxes.push(localY);
        if (axes.includes("z")) localAxes.push(localZ);

        let result = startQuat.clone();
        for (const axis of localAxes) {
            const alignUp = Vector3.Dot(axis, camUp);
            const alignRight = Vector3.Dot(axis, camRight);
            let amount = alignUp * accumY + alignRight * accumX;
            if (snapStep !== undefined) {
                amount = roundTo(amount, snapStep);
            }
            const axisRot = Quaternion.RotationAxis(axis, amount);
            result = axisRot.multiply(result);
        }
        return result;
    }

    let rotX = accumX;
    let rotY = accumY;
    if (snapStep !== undefined) {
        rotX = roundTo(rotX, snapStep);
        rotY = roundTo(rotY, snapStep);
    }
    const yRot = Quaternion.RotationAxis(Vector3.Up(), rotY);
    const xRot = Quaternion.RotationAxis(rightAxis, rotX);
    return yRot.multiply(xRot).multiply(startQuat);
}

// ============================================================================
// DragToRotate
// ============================================================================

/**
 * Module-level guard: only one DragToRotate can be actively dragging at a time.
 */
let _activeDrag: DragToRotate | null = null;

/**
 * Registry of all enabled DragToRotate targets. Used to find the most specific
 * (nearest ancestor) target for a picked mesh.
 */
const _allTargets = new Map<TransformNode, DragToRotate>();

export class DragToRotate {
    /** Fires when a drag interaction begins. */
    public readonly onDragStartObservable = new Observable<void>();
    /** Fires when a drag interaction ends. */
    public readonly onDragEndObservable = new Observable<void>();

    private readonly _target: TransformNode;
    private readonly _scene: Scene;
    private _options: DragToRotateOptions;
    private _enabled = false;
    private _disposed = false;

    // Pointer tracking
    private _pointerObserver: Observer<PointerInfo> | null = null;
    private _sceneDisposeObserver: Observer<Scene> | null = null;
    private _isDragging = false;
    private _dragPointerId: number | null = null;
    private _lastPointerX = 0;
    private _lastPointerY = 0;

    // Rotation state at drag start
    private _dragStartQuat: Quaternion | null = null;
    // Accumulated rotation amounts (radians) during the drag
    private _accumX = 0;
    private _accumY = 0;

    // Release momentum: velocity estimation during the drag + the frozen
    // glide snapshot after it. Everything the glide needs is captured at
    // POINTERUP — including the camera vectors — so an orbiting camera can't
    // bend the spin-down path.
    private _velX = new VelocityTracker();
    private _velY = new VelocityTracker();
    private _glideObserver: Observer<Scene> | null = null;
    private _glide: {
        startQuat: Quaternion;
        accumX0: number;
        accumY0: number;
        vx: number;
        vy: number;
        tau: number;
        startTime: number;
        axes: Axis[] | undefined;
        camUp: Vector3;
        camRight: Vector3;
        rightAxis: Vector3;
    } | null = null;

    constructor(target: TransformNode, scene: Scene, options?: DragToRotateOptions) {
        this._target = target;
        this._scene = scene;
        this._options = { ...options };

        ensureRotationQuaternion(this._target);
    }

    // ── Public API ──

    get enabled(): boolean { return this._enabled; }

    set enabled(value: boolean) {
        if (this._disposed || value === this._enabled) return;
        this._enabled = value;
        if (value) {
            this._activate();
        } else {
            this._deactivate();
        }
    }

    /** Update options at runtime (e.g. when axes change via commit). */
    public setOptions(options: Partial<DragToRotateOptions>): void {
        this._options = { ...this._options, ...options };
    }

    public dispose(): void {
        if (this._disposed) return;
        this._disposed = true;
        this._enabled = false;
        this._deactivate();
        this.onDragStartObservable.clear();
        this.onDragEndObservable.clear();
    }

    // ── Activation / deactivation ──

    private _activate(): void {
        _allTargets.set(this._target, this);
        this._setupPointerObserver();
        this._sceneDisposeObserver = this._scene.onDisposeObservable.addOnce(() => {
            this.dispose();
        });
    }

    private _deactivate(): void {
        _allTargets.delete(this._target);
        this._endDrag();
        // Disable (play-mode exit / dispose) stops any glide — including one
        // _endDrag just started from a mid-drag deactivation
        this._cancelGlide();
        this._teardownPointerObserver();
        if (this._sceneDisposeObserver) {
            this._scene.onDisposeObservable.remove(this._sceneDisposeObserver);
            this._sceneDisposeObserver = null;
        }
    }

    // ── Pointer observer ──

    private _setupPointerObserver(): void {
        if (this._pointerObserver) return;
        this._pointerObserver = this._scene.onPointerObservable.add((pi) => {
            switch (pi.type) {
                case PointerEventTypes.POINTERDOWN: {
                    if (pi.event.button !== 0) return;
                    if (this._isDragging || _activeDrag) return;
                    const pick = pi.pickInfo;
                    if (!pick?.hit || !pick.pickedMesh) return;
                    if (this._isMeshOfTarget(pick.pickedMesh)) {
                        this._startDrag(pi);
                    }
                    break;
                }
                case PointerEventTypes.POINTERMOVE: {
                    if (!this._isDragging) return;
                    if (this._getPointerId(pi) !== this._dragPointerId) return;
                    this._updateDrag(pi);
                    break;
                }
                case PointerEventTypes.POINTERUP: {
                    if (!this._isDragging) return;
                    if (this._getPointerId(pi) !== this._dragPointerId) return;
                    this._endDrag();
                    break;
                }
            }
        });
    }

    private _teardownPointerObserver(): void {
        if (this._pointerObserver) {
            this._scene.onPointerObservable.remove(this._pointerObserver);
            this._pointerObserver = null;
        }
    }

    private _getPointerId(pi: PointerInfo): number {
        return (pi.event as IPointerEvent).pointerId ?? 0;
    }

    // ── Drag lifecycle ──

    private _startDrag(pi: PointerInfo): void {
        ensureRotationQuaternion(this._target);

        // A re-grab claims the object mid-glide — the glide yields instantly
        this._cancelGlide();

        this._dragStartQuat = this._target.rotationQuaternion.clone();
        this._lastPointerX = pi.event.clientX;
        this._lastPointerY = pi.event.clientY;
        this._accumX = 0;
        this._accumY = 0;
        this._velX.reset();
        this._velY.reset();
        if (this._momentumEnabled()) {
            const now = performance.now();
            this._velX.push(0, now);
            this._velY.push(0, now);
        }
        this._dragPointerId = this._getPointerId(pi);
        this._isDragging = true;
        _activeDrag = this;

        this._detachCamera();
        this._options.onGrab?.();
        this.onDragStartObservable.notifyObservers();
    }

    private _updateDrag(pi: PointerInfo): void {
        if (!this._dragStartQuat) return;

        const dx = pi.event.clientX - this._lastPointerX;
        const dy = pi.event.clientY - this._lastPointerY;
        this._lastPointerX = pi.event.clientX;
        this._lastPointerY = pi.event.clientY;

        // Accumulate raw rotation amounts
        this._accumY += dx * ROTATION_SPEED; // horizontal drag → Y rotation
        this._accumX += dy * ROTATION_SPEED; // vertical drag → X rotation

        // Feed the momentum estimators (wall clock, same timebase as the
        // glide). Skipped entirely when momentum is off — editor drag
        // instances never read the trackers.
        if (this._momentumEnabled()) {
            const sampleTime = performance.now();
            this._velX.push(this._accumX, sampleTime);
            this._velY.push(this._accumY, sampleTime);
        }

        let snapStep: number | undefined;
        if (this._options.snap && pi.event.altKey) {
            snapStep = pi.event.shiftKey ? SNAP_ROTATION_COARSE : SNAP_ROTATION_FINE;
        }

        const { camUp, camRight } = this._getCameraDirections();
        this._target.rotationQuaternion = composeDragRotation(
            this._dragStartQuat,
            this._accumX,
            this._accumY,
            this._options.axes,
            camUp,
            camRight,
            this._getRightAxis(),
            snapStep,
        );
    }

    /** Returns the camera's up and right directions in world space. */
    private _getCameraDirections(): { camUp: Vector3; camRight: Vector3 } {
        const camera = this._scene.activeCamera;
        if (!camera) return { camUp: Vector3.Up(), camRight: Vector3.Right() };

        const camWorld = camera.getWorldMatrix();
        const camUp = Vector3.TransformNormal(Vector3.Up(), camWorld);
        const camRight = Vector3.TransformNormal(Vector3.Right(), camWorld);
        camUp.normalize();
        camRight.normalize();

        return { camUp, camRight };
    }

    /**
     * The unconstrained pitch axis: camera-right flattened onto XZ and
     * normalized (falls back to world X when the camera looks straight
     * down/up or is absent).
     */
    private _getRightAxis(): Vector3 {
        const camera = this._scene.activeCamera;
        let rightAxis = Vector3.Right();
        if (camera) {
            const camWorld = camera.getWorldMatrix();
            rightAxis = Vector3.TransformNormal(Vector3.Right(), camWorld);
            rightAxis.y = 0;
            const len = rightAxis.length();
            if (len > 0.001) {
                rightAxis.scaleInPlace(1 / len);
            } else {
                rightAxis = Vector3.Right();
            }
        }
        return rightAxis;
    }

    private _endDrag(): void {
        if (!this._isDragging) return;
        // Snapshot for momentum BEFORE the drag state is torn down
        this._maybeStartGlide();
        this._isDragging = false;
        _activeDrag = null;
        this._dragStartQuat = null;
        this._dragPointerId = null;
        this._accumX = 0;
        this._accumY = 0;
        this._reattachCamera();
        // NOTE: fires at release, not at glide rest — V1 semantics. The
        // editor's drag-end capture never coexists with a glide (momentum is
        // a play-mode manager option the editor doesn't pass).
        this.onDragEndObservable.notifyObservers();
    }

    // ── Release momentum (glide) ──

    /**
     * Whether the momentum option is on: a finite, positive time constant.
     * The finite check matters — NaN/Infinity would NaN the glide's closed
     * form and its stop condition would never fire (NaN <= 0 is false).
     */
    private _momentumEnabled(): boolean {
        const tau = this._options.momentumDamping ?? 0;
        return Number.isFinite(tau) && tau > 0;
    }

    /** Read the release velocities and start a glide if they warrant one. */
    private _maybeStartGlide(): void {
        if (!this._momentumEnabled() || !this._dragStartQuat) return;
        const now = performance.now();
        const clamp = (v: number) => Math.max(-MOMENTUM_MAX_RATE, Math.min(MOMENTUM_MAX_RATE, v));
        const vx = clamp(this._velX.velocity(now));
        const vy = clamp(this._velY.velocity(now));
        if (Math.abs(vx) < MOMENTUM_STOP_RATE && Math.abs(vy) < MOMENTUM_STOP_RATE) return;
        this._startGlideWithRates(vx, vy, now);
    }

    /**
     * Begin the spin-down from the current drag state with the given angular
     * rates (rad/s on the accumX/accumY channels). Freezes EVERYTHING the
     * composition needs — start quaternion, accumulated angles, and the
     * camera vectors — so an orbiting camera can't bend the glide. The angle
     * at time t is evaluated in closed form from total elapsed time (never
     * integrated per frame): identical trajectories at any frame rate.
     */
    private _startGlideWithRates(vx: number, vy: number, nowMs: number): void {
        if (!this._dragStartQuat) return;
        this._cancelGlide();

        const { camUp, camRight } = this._getCameraDirections();
        // Frozen unconstrained pitch axis — same derivation as _updateDrag
        const rightAxis = this._getRightAxis();

        this._glide = {
            startQuat: this._dragStartQuat.clone(),
            accumX0: this._accumX,
            accumY0: this._accumY,
            vx,
            vy,
            tau: this._options.momentumDamping ?? 0,
            startTime: nowMs,
            axes: this._options.axes ? [...this._options.axes] : undefined,
            camUp: camUp.clone(),
            camRight: camRight.clone(),
            rightAxis: rightAxis.clone(),
        };

        this._glideObserver = this._scene.onBeforeRenderObservable.add(() => this._glideStep());
    }

    private _glideStep(): void {
        const g = this._glide;
        if (!g) return;
        const t = (performance.now() - g.startTime) / 1000;

        const done = Math.abs(glideRate(g.vx, g.tau, t)) < MOMENTUM_STOP_RATE
            && Math.abs(glideRate(g.vy, g.tau, t)) < MOMENTUM_STOP_RATE;

        const accumX = g.accumX0 + glideSweep(g.vx, g.tau, t);
        const accumY = g.accumY0 + glideSweep(g.vy, g.tau, t);

        // Recompose exactly like the drag, against the FROZEN snapshot
        // (no snapStep during glide — modifier keys belong to active gestures)
        this._target.rotationQuaternion = composeDragRotation(
            g.startQuat,
            accumX,
            accumY,
            g.axes,
            g.camUp,
            g.camRight,
            g.rightAxis,
        );

        if (done) {
            this._cancelGlide();
        }
    }

    /** Stop any running glide. Safe to call at any time. */
    private _cancelGlide(): void {
        if (this._glideObserver) {
            this._scene.onBeforeRenderObservable.remove(this._glideObserver);
            this._glideObserver = null;
        }
        this._glide = null;
    }

    // ── Camera detach / reattach ──

    private _detachCamera(): void {
        const camera = this._scene.activeCamera;
        if (camera) {
            camera.detachControl();
        }
    }

    private _reattachCamera(): void {
        const camera = this._scene.activeCamera;
        const canvas = this._scene.getEngine().getRenderingCanvas();
        if (camera && canvas) {
            camera.attachControl(canvas, true);
        }
    }

    // ── Mesh ownership detection ──

    private _isMeshOfTarget(mesh: AbstractMesh): boolean {
        let current: TransformNode | null = mesh;
        while (current) {
            if (_allTargets.has(current)) {
                return current === this._target;
            }
            current = current.parent as TransformNode | null;
        }
        return false;
    }
}
